import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as SmartGateDeniedIcon } from "assets/images/smart-gate-denied.svg";
import { ReactComponent as SmartGateOkIcon } from "assets/images/smart-gate-ok.svg";
import {
  Box,
  GhostIconButton,
  Loader,
  SecondaryButton,
  Text,
} from "components";
import Drawer from "components/smaltOverwritten/Drawer";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import useWindowDimensions from "hooks/windowDimensions";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getTranslationsStyle, isArabic } from "utils/arabisation.util";

export const CircleButtonStyled = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1200px;
  height: 700px;
  border-radius: 50%;
  background-color: #686ea0;
  border: none;
  position: absolute;
  left: ${({ isArabic }) => (!isArabic ? "-540px" : "unset")};
  right: ${({ isArabic }) => (isArabic ? "-540px" : "unset")};
  top: -25px;
  z-index: -1;
`;

const ValidationDrawer = ({
  isOpen,
  onClose,
  validationResult,
  isValidating,
  onContinue,
  scanAnalytics,
  nationality,
  gender,
  dateOfBirth,
}) => {
  const { sendAnalytics } = useTrackPageAnalytics();
  const { t, i18n } = useTranslation();
  const { height, width } = useWindowDimensions();
  const [copySuccess, setCopySuccess] = useState(false);
  const resizeHandleRef = useRef(null);
  // Track if we've already incremented for this result
  const hasIncrementedRef = useRef(false);

  const handleScanAnother = () => {
    if (scanAnalytics && scanAnalytics.resetAnalytics) {
      scanAnalytics.resetAnalytics();
    }
    onClose();
  };

  const copyErrorToClipboard = () => {
    if (validationResult?.messageLong) {
      navigator.clipboard.writeText(validationResult.messageLong).then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 3000);
      });
    }
  };

  const calculateScale = () => {
    // Linear interpolation between 375px (scale 1) and 414px (scale 1.3)
    const scale = 1 + (0.3 * (width - 375)) / (414 - 375);
    // Clamp the value between 1 and 1.3
    return Math.min(1.3, Math.max(1, scale));
  };

  const shouldApplyArabicLayout = isArabic(i18n);

  const renderContent = () => {
    if (isValidating) {
      return (
        <Box
          d="flex"
          direction="column"
          justifyContent="center"
          alignItems="center"
          flex="1"
          p="l"
          gap="m"
        >
          <Loader />
          <Text
            fontSize="m"
            color="primary"
            style={getTranslationsStyle(i18n, { textAlign: "center" })}
          >
            {t("smart_gate.validating_passport")}
          </Text>
        </Box>
      );
    }

    if (!validationResult) {
      return null;
    }

    if (validationResult.status === "error") {
      if (scanAnalytics && scanAnalytics.incrementError) {
        scanAnalytics.incrementError();
      }
      return (
        <Box d="flex" flex="1" direction="column">
          <Box
            h="48px"
            backgroundColor="error"
            d="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Text
              fontSize="m"
              color="textQuaternary"
              fontWeight="bold"
              mT="xxs"
              style={getTranslationsStyle(i18n, { textAlign: "center" })}
            >
              {t("smart_gate.system_error")}
            </Text>
          </Box>
          <Box
            borderColor="primary"
            flex="1"
            d="flex"
            direction="column"
            justifyContent="center"
          >
            <Box flex="1" d="flex" alignItems="center" justifyContent="center">
              <Box pH="m" d="flex" alignItems="center" justifyContent="center">
                <Text
                  style={getTranslationsStyle(i18n, { textAlign: "center" })}
                  fontSize="m"
                  pH="base"
                >
                  {validationResult.title}
                </Text>
              </Box>
            </Box>
            <Box flex="1">
              <Box
                d="flex"
                direction="column"
                gap="s"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                pH="base"
              >
                <Text
                  fontSize="m"
                  fontWeight="bold"
                  textAlign="center"
                  style={getTranslationsStyle(i18n, { textAlign: "center" })}
                >
                  {t("smart_gate.unable_to_process")}
                </Text>
                <Text
                  fontSize="m"
                  textAlign="center"
                  style={getTranslationsStyle(i18n)}
                >
                  {validationResult.message || t("smart_gate.try_again_later")}
                </Text>
              </Box>
              <Box
                d="flex"
                gap="base"
                w="100%"
                alignItems="center"
                justifyContent="center"
                mT="l"
              >
                <SecondaryButton
                  onClick={() => {
                    sendAnalytics({
                      key: "smart-gate-click-scan-another",
                      type: "event",
                      name: "User clicked on scan another button",
                    });
                    handleScanAnother();
                  }}
                >
                  <Text style={getTranslationsStyle(i18n)} fontSize="m">
                    {t("smart_gate.try_again")}
                  </Text>
                </SecondaryButton>
                {validationResult.messageLong && (
                  <SecondaryButton onClick={copyErrorToClipboard}>
                    <Text fontSize="m" style={getTranslationsStyle(i18n)}>
                      {copySuccess
                        ? t("smart_gate.copied")
                        : t("smart_gate.copy_error")}
                    </Text>
                  </SecondaryButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    const isEligible =
      validationResult.status === "success" && validationResult.data.result;

    return (
      <Box d="flex" flex="1" direction="column" h="100%" overflow="hidden">
        <Box
          h="48px"
          backgroundColor={isEligible ? "smartGateEligible" : "smartGateDenied"}
          d="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Text
            fontSize="l"
            color="textQuaternary"
            fontWeight="bold"
            style={getTranslationsStyle(i18n)}
          >
            {isEligible
              ? t("smart_gate.access_confirmed")
              : t("smart_gate.access_denied")}
          </Text>
        </Box>
        <Box
          h="100%"
          flex="1"
          d="flex"
          direction="column"
          justifyContent="center"
          b
        >
          <Box
            flex="1"
            d="flex"
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              d="flex"
              alignItems="center"
              justifyContent="center"
              maxW="200px"
              flex="0.3"
              mT="m"
            >
              <Text
                fontSize="l"
                fontWeight="bold"
                textAlign="center"
                style={getTranslationsStyle(i18n)}
              >
                {isEligible
                  ? t("smart_gate.eligible_title")
                  : t("smart_gate.not_eligible_title")}
              </Text>
            </Box>
            <Box flex="1" d="flex" alignItems="center" justifyContent="center">
              {isEligible ? (
                <SmartGateOkIcon
                  style={{ transform: `scale(${calculateScale()})` }}
                />
              ) : (
                <SmartGateDeniedIcon
                  style={{
                    transform: `scale(${
                      calculateScale() - 0.2
                    }) translateY(-15px)`,
                  }}
                />
              )}
            </Box>
          </Box>
          <Box
            d="flex"
            direction="column"
            justifyContent="flex-end"
            style={{ zIndex: 2 }}
            h="100%"
            flex="1"
          >
            <Box
              d="flex"
              direction="column"
              alignItems="center"
              justifyContent="center"
              w="100vw"
              h="80%"
            >
              <Box
                d="flex"
                direction="column"
                gap="s"
                justifyContent="center"
                textAlign="center"
                w="80vw"
                position="relative"
              >
                <Text
                  fontSize="l"
                  fontWeight="bold"
                  textAlign="left"
                  color="textQuaternary"
                  style={getTranslationsStyle(i18n)}
                  position="relative"
                >
                  <CircleButtonStyled isArabic={shouldApplyArabicLayout} />
                  {t("smart_gate.pro_tip")}
                </Text>
                <Text
                  fontSize="l"
                  color="textQuaternary"
                  style={getTranslationsStyle(i18n)}
                >
                  {isEligible
                    ? t("smart_gate.eligible_description")
                    : t("smart_gate.not_eligible_description")}
                </Text>
              </Box>
            </Box>
            <Box
              d="flex"
              justifyContent="center"
              w="100%"
              mT="l"
              style={{
                zIndex: 2,
                marginBottom: "10%",
              }}
            >
              <Box w="80vw" d="flex" gap="base">
                <SecondaryButton
                  w="100%"
                  onClick={onContinue}
                  style={{ backgroundColor: "#9898BB", borderColor: "#9898BB" }}
                >
                  <Text
                    fontSize="m"
                    color="textQuaternary"
                    style={getTranslationsStyle(i18n, { textAlign: "center" })}
                  >
                    {t("smart_gate.continue")}
                  </Text>
                </SecondaryButton>
                <SecondaryButton w="100%" onClick={handleScanAnother}>
                  <Text
                    fontSize="m"
                    style={getTranslationsStyle(i18n, { textAlign: "center" })}
                  >
                    {t("smart_gate.scan_another")}
                  </Text>
                </SecondaryButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (
      isOpen &&
      scanAnalytics &&
      validationResult &&
      validationResult.status === "success" &&
      !hasIncrementedRef.current
    ) {
      const isEligible = validationResult.data.result;
      if (isEligible) {
        scanAnalytics.incrementEligible();
      } else {
        scanAnalytics.incrementNonEligible();
      }
      scanAnalytics.markTotalTimeSpent();
      hasIncrementedRef.current = true;

      // Use nationality, gender, and dateOfBirth props directly
      scanAnalytics.sendAnalyticsIfNeeded({
        reason: "drawer",
        nationality,
        gender,
        dateOfBirth,
      });
    }
    if (!isOpen) {
      hasIncrementedRef.current = false;
    }
  }, [
    isOpen,
    scanAnalytics,
    validationResult,
    nationality,
    gender,
    dateOfBirth,
  ]);

  useEffect(() => {
    if (
      isOpen &&
      scanAnalytics &&
      validationResult &&
      validationResult.status === "error"
    ) {
      scanAnalytics.sendAnalyticsIfNeeded({
        reason: "drawer",
        error: true,
        errorCount: 1,
        nationality,
        gender,
        dateOfBirth,
      });
    }
  }, [
    isOpen,
    scanAnalytics,
    validationResult,
    nationality,
    gender,
    dateOfBirth,
  ]);

  return (
    <Drawer isOpen={isOpen} translationAmount={0} minimumContentHeight={height}>
      <Box d="flex" direction="column" alignItems="center" pH="m">
        <Box
          ref={resizeHandleRef}
          h="24px"
          d="flex"
          justifyContent="center"
          alignItems="center"
          style={{ cursor: "pointer" }}
        >
          <Box
            w="40px"
            h="4px"
            backgroundColor="resizeHandle"
            borderRadius="2px"
            style={{ opacity: 0.8 }}
          />
        </Box>
        <Box
          h="40px"
          d="flex"
          alignItems="center"
          justifyContent="space-between"
          w="100%"
          borderColor="lightNeutrals"
          mB="s"
        >
          <GhostIconButton
            name="clearclose"
            color="inherit"
            iconSize="l"
            onClick={() => {
              sendAnalytics({
                key: "smart-gate-validation-click-close",
                type: "event",
                name: "User clicked on 'X' button in Validation drawer and the scanning process is started again",
              });
              handleScanAnother();
            }}
          />
        </Box>
      </Box>
      <Box
        d="flex"
        direction="column"
        style={{ height: "calc(100dvh - 76px)" }}
      >
        {renderContent()}
      </Box>
    </Drawer>
  );
};

export default ValidationDrawer;
